<template>
    <p>
        {{ $t('guide.guide_popover_step_9_desc') }}
    </p>
    <div :class="['pt-tooltip-wrap', (readOnly || noCampaigns) && isHoverButton && 'hover']">
        <span class="tooltip" v-if="disabledTip">
            <i>{{ disabledTip }}</i>
        </span>
        <span
            ref="tooltipButton"
            style="
                 {
                    display: inline-block;
                }
            "
        >
            <pt-button
                color="lightblue"
                :type="props.step.done ? 'outline' : 'contain'"
                @click="handleClick"
                :disabled="noCampaigns || readOnly"
            >
                {{ $t('guide.guide_popover_step_9_btn_publish') }}
            </pt-button>
        </span>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useGuideStore } from '@/pages/master/store/guide.store';
import { useProfileInfo, Product } from '@models/profile/useCurrentProfile';
import type { Profile } from '@models/profile/profileList';
import useShowAuthDialog from '@master/hooks/service/useShowAuthDialog';
import useEventBus from '@assets/plugins/eventBus';
import { useI18n } from 'vue-i18n';
import { GuideStep } from '@master/store/guide.store';

interface Props {
    step: GuideStep;
}

const props = defineProps<Props>();

const emit = defineEmits(['trackEvent']);

const router = useRouter();
const { eventBus } = useEventBus();
const { hideGuidePopover } = useGuideStore();
const { t } = useI18n();
const {
    ptxModuleUsage,
    profileInfo,
    experienceProduct,
    profileProductMenuList,
    setProfileProductMenu
} = useProfileInfo();
const { checkAuth } = useShowAuthDialog();
const tooltipButton = ref();
const isHoverButton = ref(false);
const noCampaigns = computed(() => {
    return ptxModuleUsage.value?.campaigns === 0;
});

const experienceMenu = computed(() => {
    return profileProductMenuList.value?.['experience']?.find(item => item.key === 'CAMPAIGN');
});

const readOnly = computed(() => {
    return profileInfo.value?.ptxUserRole
        ? !['owner', 'admin'].includes(profileInfo.value?.ptxUserRole)
        : false;
});

const disabledTip = computed(() => {
    return noCampaigns.value
        ? t('guide.guide_popover_step_9_disable_tip')
        : readOnly
        ? t('experience.message.no_publish_access_tips', {
              owner: profileInfo.value?.ownerEmail
          })
        : '';
});

onMounted(() => {
    tooltipButton.value?.addEventListener('mouseenter', handleMouseEnter, false);
    tooltipButton.value?.addEventListener('mouseleave', handleMouseLeave, false);
});

onUnmounted(() => {
    tooltipButton.value?.removeEventListener('mouseenter', handleMouseEnter, false);
    tooltipButton.value?.removeEventListener('mouseleave', handleMouseLeave, false);
});

function handleClick() {
    eventBus.$emit('hideVerifyDialog');
    eventBus.$emit('hideSetup');

    checkAuth(experienceProduct.value as Product, profileInfo.value as Profile, () => {
        emit('trackEvent', 'guide_publish_click', {
            has_completed: props.step.done ? 1 : 0
        });

        if (router.currentRoute.value?.name === 'ExperienceList') {
            eventBus.$emit('showTip', 'experiencePublish');
        } else if (router.currentRoute.value?.name === 'ExperienceSetting') {
            eventBus.$emit('scrollToModule', 'CREATIVE'); // 滚动到最顶部
            setTimeout(() => {
                eventBus.$emit('showTip', 'experiencSettingPublish');
            }, 100);
        } else {
            router.push({
                name: 'ExperienceList',
                params: {
                    from: 'start_guide',
                    needQuery: 'true'
                }
            });
            eventBus.$emit('changeProfileMenuItem', experienceMenu.value);
            setProfileProductMenu(experienceMenu.value);
        }

        hideGuidePopover();
    });
}

function handleMouseEnter() {
    isHoverButton.value = true;
}

function handleMouseLeave() {
    isHoverButton.value = false;
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
</style>
